import { useQuery } from '@tanstack/react-query';
import { useEffect, type ComponentType } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { policyholders, type Policyholder } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatCpfCnpj, formatDate, formatMoney } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { DetailError, DetailLayout, DetailLoading } from '@/shared/ui/DetailLayout';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import { rotuloSituacaoDeCredito } from '../rotulos';
import { CadastroCompleto } from './CadastroCompleto';
import { SecaoAnalises } from './AnalisesDoTomador';
import {
  Apolices,
  Ccg,
  CondicoesComerciais,
  Documentos,
  Filiais,
  HistoricoDeAlteracoes,
  HistoricoDeConsultas,
  LimitesETaxas,
  Socios,
} from './Secoes';
import { Corretor, GrupoEconomico } from './GrupoENomeacao';
import { RegrasExecutadas } from './RegrasEHistorico';
import { SecaoLeiloeiro } from './SecaoLeiloeiro';
import { EnviosDaSeguradora, RevisaoDeLimite } from './RevisaoEEnvios';

interface SecaoDoMenu {
  chave: string;
  titulo: string;
  /** Permissoes que abrem a secao. Vazio: a de ver o tomador basta. */
  exige: string[];
  Componente: ComponentType<{ tomador: Policyholder }>;
  /** Secao que so vale para alguns tomadores, como a do leiloeiro (pessoa fisica). */
  aplica?: (tomador: Policyholder) => boolean;
}

/**
 * O menu lateral do detalhe do tomador, com os treze itens do OnPoint e na mesma
 * ordem. Cada item aparece para quem tem a permissao da secao: a corretora ve, como
 * no OnPoint, Cadastro Completo, Filiais, Limites e Taxas, Apolices e Analises.
 *
 * Corretor (P-TOM-C5) e a nomeacao do corretor como pedido. No OnPoint o "Corretor
 * Nomeado" fica no Cadastro Completo e o pedido corre na esteira Nomeacao; aqui a
 * secao vem logo depois do Cadastro Completo, sem mudar a ordem dos treze itens.
 *
 * Revisao de Limite e Envios da seguradora (P-EST-2) vem depois de Limites e Taxas, so
 * para quem tem a permissao: no OnPoint o pedido de revisao corre na esteira Credito e
 * o envio ao corretor sai do cartao; aqui a corretora pede e ve o que recebeu.
 */
const secoesDoTomador: SecaoDoMenu[] = [
  {
    chave: 'cadastro',
    titulo: 'Cadastro Completo',
    exige: [],
    Componente: CadastroCompleto,
  },
  { chave: 'corretor', titulo: 'Corretor', exige: [], Componente: Corretor },
  // Dados do Leiloeiro (P-NCT-3): so no tomador pessoa fisica, como o cartao do OnPoint.
  {
    chave: 'leiloeiro',
    titulo: 'Dados do Leiloeiro',
    exige: [],
    Componente: SecaoLeiloeiro,
    aplica: (tomador) => tomador.document_kind === 'cpf',
  },
  { chave: 'filiais', titulo: 'Filiais', exige: [], Componente: Filiais },
  {
    chave: 'grupo',
    titulo: 'Grupo Economico',
    exige: ['tomadores.ficha.view'],
    Componente: GrupoEconomico,
  },
  {
    chave: 'regras',
    titulo: 'Regras Executadas',
    exige: ['credito.motor.view'],
    Componente: RegrasExecutadas,
  },
  { chave: 'limites', titulo: 'Limites e Taxas', exige: [], Componente: LimitesETaxas },
  {
    chave: 'revisao-limite',
    titulo: 'Revisao de Limite',
    exige: ['credito.revisao_limite.view'],
    Componente: RevisaoDeLimite,
  },
  {
    chave: 'envios',
    titulo: 'Envios da seguradora',
    exige: ['esteiras.envio.view'],
    Componente: EnviosDaSeguradora,
  },
  {
    chave: 'condicoes',
    titulo: 'Condicoes Comerciais',
    exige: ['tomadores.ficha.view'],
    Componente: CondicoesComerciais,
  },
  { chave: 'ccg', titulo: 'CCG', exige: ['tomadores.ficha.view'], Componente: Ccg },
  {
    chave: 'apolices',
    titulo: 'Apolices',
    exige: ['apolices.apolice.view'],
    Componente: Apolices,
  },
  {
    chave: 'socios',
    titulo: 'Socios / Representantes',
    exige: ['tomadores.ficha.view'],
    Componente: Socios,
  },
  {
    chave: 'documentos',
    titulo: 'Documentos',
    exige: ['documentos.documento.view'],
    Componente: Documentos,
  },
  // Analises (P-TOM-C4): o processo do proprio tomador e, abaixo, as analises de
  // credito das operacoes. A secao abre para quem ve o tomador, como no OnPoint.
  { chave: 'analises', titulo: 'Analises', exige: [], Componente: SecaoAnalises },
  {
    chave: 'alteracoes',
    titulo: 'Historico de Alteracoes',
    exige: ['tomadores.ficha.view'],
    Componente: HistoricoDeAlteracoes,
  },
  {
    chave: 'consultas',
    titulo: 'Historico de Consultas',
    exige: ['credito.motor.view'],
    Componente: HistoricoDeConsultas,
  },
];

/**
 * Detalhe do tomador ("Visualizar" da lista).
 *
 * Reune, em um lugar, o cadastro completo e as secoes do OnPoint. Cada secao le a
 * propria rota, e o servidor confere empresa, escopo da corretora e permissao em
 * cada uma: a tela esconde o que nao abre, e nao decide o acesso.
 *
 * A moldura (cabecalho, menu lateral e a secao no endereco) e o `DetailLayout`
 * comum (P-FUN-2b), que nasceu desta tela.
 */
export function PolicyholderDetailPage() {
  const { policyholderId = '' } = useParams();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [, setParametros] = useSearchParams();

  const consulta = useQuery({
    queryKey: ['policyholder', policyholderId],
    queryFn: ({ signal }) => policyholders.get(policyholderId, signal),
    enabled: policyholderId !== '',
  });

  const visiveis = secoesDoTomador.filter((secao) =>
    secao.exige.every((permissao) => can(permissao)),
  );

  // O "Upload Manual CCG" leva a secao de documentos.
  useEffect(() => {
    function irPara(evento: Event) {
      const chave = (evento as CustomEvent<string>).detail;
      const proximos = new URLSearchParams(window.location.search);
      proximos.set('secao', chave);
      setParametros(proximos, { replace: true });
    }
    window.addEventListener('tomador:secao', irPara);
    return () => window.removeEventListener('tomador:secao', irPara);
  }, [setParametros]);

  if (consulta.isPending) return <DetailLoading />;

  if (consulta.isError) {
    return (
      <DetailError
        title="Tomador"
        alertTitle="Nao foi possivel abrir o tomador"
        message={consulta.error.message}
        onBack={() => void navigate('/tomadores')}
      />
    );
  }

  const tomador = consulta.data;

  return (
    <DetailLayout
      navLabel="Secoes do tomador"
      sections={visiveis
        .filter((secao) => !secao.aplica || secao.aplica(tomador))
        .map(({ chave, titulo, Componente }) => ({
          key: chave,
          title: titulo,
          render: () => <Componente tomador={tomador} />,
        }))}
      title={tomador.legal_name}
      description={`${formatCpfCnpj(tomador.document)} . Tomador ${tomador.number}`}
      meta={
        <>
          <StatusBadge kind="cadastro" value={tomador.status} />
          {tomador.credit_status ? (
            <Badge tone={tomador.credit_status === 'aprovado' ? 'success' : 'danger'}>
              {rotuloSituacaoDeCredito(tomador.credit_status)}
            </Badge>
          ) : null}
          {tomador.blocked_at ? (
            <Badge tone="danger">Bloqueado em {formatDate(tomador.blocked_at)}</Badge>
          ) : null}
          {tomador.ignore_ccg_signature ? (
            <Badge tone="warning">Ignorar CCG</Badge>
          ) : null}
          {tomador.blocked_by_restrictive ? (
            <Badge tone="danger">Bloqueado por restritivos</Badge>
          ) : null}
          <Badge tone="neutral">
            Limite geral{' '}
            {tomador.credit_limit.amount > 0
              ? formatMoney(tomador.credit_limit)
              : 'sem limite'}
          </Badge>
        </>
      }
      actions={
        <Link
          to="/tomadores"
          className="inline-flex h-10 items-center rounded-md px-3 text-sm font-medium text-ink-600 transition-colors duration-(--duration-fast) hover:bg-ink-100"
        >
          Voltar para a lista
        </Link>
      }
      banner={
        tomador.block_reason ? (
          <Alert tone="danger" title="Tomador bloqueado">
            {tomador.block_reason}
          </Alert>
        ) : null
      }
    />
  );
}
